import { useGetNodeContentQuery } from '@/api/nodes/queries/get-node-content-query'
import { useGetNodeQuery } from '@/api/nodes/queries/get-node-query'
import { buildNodePathAbove, useDirectoryContext } from '@/features/drive/entities/directory/directory-context'
import { useNodeItemContext } from '@/features/drive/entities/node/node-item-context'
import { useMoveNode } from '@/features/drive/functionalities/actions/node/core/node-move-action/utils/use-move-node'
import { useDirectorySelectionContext } from '@/features/drive/functionalities/selection/directory-selection-context'
import { toast } from '@/features/toast/toast'
import { run } from 'shared'
import { useCallback, useMemo, useRef, useState } from 'react'

export interface IUseNodeMoveActionSubMenuProps {
    path: string
    level: number
    mode: 'node' | 'selected'
}

export const useNodeMoveActionSubMenu = ({ path: nodePath, level, mode }: IUseNodeMoveActionSubMenuProps) => {
    const [isOpen, setIsOpen] = useState(false)
    const isHoveredRef = useRef(false)

    const { nodePath: environmentDirectoryPath } = useDirectoryContext()

    const {
        methods: { getIsSelected },
        move: { moveSelected },
    } = useDirectorySelectionContext()

    // TODO: @aron Not good that this is used here, we should rather split the move action into a version handling selected nodes and another one handling single nodes.
    const { node: singleTargetNode } = useNodeItemContext({ strict: false }) || {}

    const { moveNodes } = useMoveNode()

    const { data: content } = useGetNodeContentQuery({
        nodePath,
        enabled: isOpen,
        searchParams: { type: 'DIRECTORY' },
    })

    const { data: directory } = useGetNodeQuery({ nodePath, enabled: isOpen })

    const parentPath = buildNodePathAbove(nodePath)

    const { data: parentNode } = useGetNodeQuery({ nodePath: parentPath, enabled: isOpen })

    const isDriveRoot = nodePath === '/'

    const onMoveHereClick = useCallback(() => {
        if (!environmentDirectoryPath) return

        const promise = run((): Promise<unknown> | null => {
            switch (mode) {
                case 'selected':
                    return moveSelected({ newDirectoryPath: nodePath }) ?? null

                case 'node': {
                    if (!singleTargetNode) {
                        toast.error('No target node selected')

                        return null
                    }

                    return moveNodes?.({ newDirectoryPath: nodePath, paths: [singleTargetNode.path] }) ?? null
                }
            }
        })

        if (!promise) return

        // moveSelected works optimistically, so we don't need to wait for the mutation to finish
        setIsOpen(false)
    }, [environmentDirectoryPath, moveNodes, moveSelected, nodePath, singleTargetNode, mode])

    const getIsSelf = useCallback(
        (nodePath: string) => {
            const nodePathWithSlash = nodePath.startsWith('/') ? nodePath : `/${nodePath}`

            return nodePathWithSlash === environmentDirectoryPath
        },
        [environmentDirectoryPath]
    )

    const filteredContent = useMemo(() => {
        switch (mode) {
            case 'selected':
                return content?.filter((node) => {
                    const isTargetNode = getIsSelected(node.uuid, nodePath)

                    return !isTargetNode && !node.systemNode
                })

            case 'node': {
                if (!singleTargetNode) {
                    toast.error('No target node selected')

                    return null
                }
                return content?.filter((node) => {
                    const isTargetNode = node.uuid === singleTargetNode.uuid

                    return !isTargetNode && !node.systemNode
                })
            }
        }
    }, [content, getIsSelected, mode, nodePath, singleTargetNode])

    const isCurrentMenu = getIsSelf(nodePath)
    const showMoveHereItem = !isCurrentMenu
    const showGoUpItem = !isDriveRoot && level <= 0 && isCurrentMenu

    const handleOpenChange = useCallback((v: boolean, isEntrypoint?: boolean) => {
        // Without the second condition, the dropdown menu would open and close in the file preview view
        if (!v && !isHoveredRef.current) setIsOpen(false)
        else {
            // Prevent fetching every nodes content when the user runs over the menu with their mouse.
            setTimeout(
                () => {
                    if (isHoveredRef.current) setIsOpen(true)
                },
                isEntrypoint ? 0 : 50
            )
        }
    }, [])

    const handleArrowRight = useCallback(() => {
        setIsOpen(true)
    }, [])

    const handleMouseEnter = useCallback(() => {
        isHoveredRef.current = true
    }, [])

    const handleMouseLeave = useCallback(() => {
        isHoveredRef.current = false
    }, [])

    return {
        isOpen,
        directory,
        parentNode,
        parentPath,
        filteredContent,
        showMoveHereItem,
        showGoUpItem,
        isDriveRoot,
        onMoveHereClick,
        handleOpenChange,
        handleArrowRight,
        handleMouseEnter,
        handleMouseLeave,
        getIsSelf,
    }
}
